/* The studio's one "Back with you" pile: work a later stage sent back to this
   one, read from the registry. A policy design asked about sits in `design`
   with an open, unanswered-by-scoping question; once scoping answers it is
   still `design` with the question open until design accepts it; a policy
   validation sent back returns to validation, to the person who sent it — so
   each pile is one filter over the same rows. */
import type {
  MentionThreadRow,
  PolicyRole,
  PolicyStage,
  PolicySummary,
} from '@worker/policy-builder/client'
import { POLICY_STAGE_LABELS } from '@worker/policy-builder/client'
import { formatDistanceToNowStrict, parseISO } from 'date-fns'

export interface BackWithYouItem {
  /** What the panel groups it under: a validation send-back blocks, a design
   *  or scoping ask is a question, a tag is a mention. */
  kind: 'blocker' | 'question' | 'mention'
  /** A unique list key: a policy can hold a handoff and several threads. */
  key: string
  /** The policy it is on. */
  id: string
  title: string
  /** Who sent it back. */
  from: string
  when: string
  /** Where it belongs, when the stage tracks one. */
  context?: string
  /** The ask, or the finding, quoted on the card. */
  detail: string
  /** What it moves, or what was kept with it. */
  note: string
  noteIcon: string
  action: string
  to: string
  /** A thread with activity you have not seen in this browser. */
  unread?: boolean
  /** The thread a mention card replies into, inline. */
  thread?: { id: string; itemId: string }
}

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

const withScoping = (row: PolicySummary): BackWithYouItem => ({
  kind: 'question',
  key: `handoff:${row.id}`,
  id: row.id,
  title: row.title,
  from: row.askedBy ?? 'Design',
  when: formatDistanceToNowStrict(
    parseISO(row.waitingSince ?? row.stageChangedAt),
    {
      addSuffix: true,
    },
  ),
  detail: `Design sent ${plural(row.openQuestions, 'question')} back before building on this scope.`,
  note: 'Answer each, then send it back to design',
  noteIcon: 'i-ph-target',
  action: 'Answer and return to design',
  to: `/policy-studio/scoping/${row.id}`,
})

const answered = (row: PolicySummary): BackWithYouItem => ({
  kind: 'question',
  key: `handoff:${row.id}`,
  id: row.id,
  title: row.title,
  from: 'Scoping answered your question',
  when: formatDistanceToNowStrict(parseISO(row.stageChangedAt), {
    addSuffix: true,
  }),
  detail: `Scoping answered ${plural(row.openQuestions, 'question')}; accept each into the tree to close it.`,
  note: 'Tree and cases kept. Reopens where you left off.',
  noteIcon: 'i-ph-archive',
  action: 'Reopen where you left off',
  to: `/policy-studio/design/${row.id}`,
})

const sentBack = (
  row: PolicySummary,
  team: 'scoping' | 'design',
): BackWithYouItem => ({
  kind: 'blocker',
  key: `handoff:${row.id}`,
  id: row.id,
  title: row.title,
  from: 'Validation',
  when: formatDistanceToNowStrict(parseISO(row.stageChangedAt), {
    addSuffix: true,
  }),
  detail: `Validation failed this policy and sent it back to ${team}.`,
  note: "Validation's issues and evidence are at the top of the policy.",
  noteIcon: 'i-ph-arrow-u-up-left',
  action: 'Read the issues',
  to: `/policy-studio/${team}/${row.id}`,
})

const returned = (row: PolicySummary): BackWithYouItem => ({
  kind: 'question',
  key: `handoff:${row.id}`,
  id: row.id,
  title: row.title,
  from: row.queue.lastDecision?.actor ?? 'Design',
  when: formatDistanceToNowStrict(parseISO(row.stageChangedAt), {
    addSuffix: true,
  }),
  detail:
    row.queue.returning === 'changed'
      ? 'Changed and approved again after your send-back.'
      : `Answered your send-back without a change: ${row.queue.lastDecision?.note ?? ''}`.trim(),
  note: 'What you sent back is at the top of the policy.',
  noteIcon: 'i-ph-arrow-u-down-right',
  action: 'Validate again',
  to: `/policy-studio/validation/${row.id}`,
})

/** The registry stages the panel draws from, fetched once for all of it: the
 *  handoff piles come from scoping, design and validation, the user's own work
 *  from every stage that still has an owner. */
export const BACK_WITH_YOU_STAGES = ['scoping', 'design', 'validation'] as const

const HANDOFF_STAGES: readonly PolicyStage[] = ['scoping', 'design']

/* Where each stage's detail page lives, for a link into a thread. */
export const STAGE_PATH: Record<PolicyStage, string> = {
  scoping: '/policy-studio/scoping',
  design: '/policy-studio/design',
  validation: '/policy-studio/validation',
  published: '/policy-studio/maintenance',
  dismissed: '/policy-studio/scoping',
}

/** The role whose owner currently carries a stage's work; published and
 *  dismissed have no one still carrying them. */
export const STAGE_ROLE: Partial<Record<PolicyStage, PolicyRole>> = {
  scoping: 'scoping',
  design: 'design',
  validation: 'validation',
}

const awaitingPublish = (row: Pick<PolicySummary, 'stage' | 'publishedAt'>) =>
  row.stage === 'published' && row.publishedAt === null

/** Who carries a policy now. One approved but not yet published is still
 *  validation's to publish, so it stays in their queues (B2). */
export const carryingRole = (
  row: Pick<PolicySummary, 'stage' | 'publishedAt'>,
): PolicyRole | undefined =>
  awaitingPublish(row) ? 'validation' : STAGE_ROLE[row.stage]

/** The page a policy is worked on now: awaiting publish is still validation's. */
export const policyPath = (
  row: Pick<PolicySummary, 'stage' | 'publishedAt'>,
): string =>
  awaitingPublish(row) ? STAGE_PATH.validation : STAGE_PATH[row.stage]

/** The stage label, with "Ready to publish" for an approved, unpublished policy. */
export const stageLabelOf = (
  row: Pick<PolicySummary, 'stage' | 'publishedAt'>,
): string =>
  awaitingPublish(row) ? 'Ready to publish' : POLICY_STAGE_LABELS[row.stage]

/** Rows `email` is the current owner of, at whatever stage they are in now. */
export const assignedTo = (
  rows: PolicySummary[],
  email: string,
): PolicySummary[] => {
  const mine = email.toLowerCase()
  return rows.filter((row) => {
    const role = carryingRole(row)
    return !!role && row.owners[role]?.toLowerCase() === mine
  })
}

/** The `limit` policies `email` has carried longest in their current stage —
 *  the ones most likely to be stuck on them. */
export const topWork = (
  rows: PolicySummary[],
  email: string,
  limit = 3,
): PolicySummary[] =>
  assignedTo(rows, email)
    .toSorted((a, b) => a.stageChangedAt.localeCompare(b.stageChangedAt))
    .slice(0, limit)

/** The handoff piles `path`'s stage owns, for someone who has picked no teams.
 *  Sources and scoping share one: a question lands on the document it came
 *  out of and on the policy inside it, and the same person works both. */
const teamsOfPath = (path: string): PolicyRole[] => {
  if (path.startsWith('/policy-studio/design')) return ['design']
  if (path.startsWith('/policy-studio/validation')) return ['validation']
  if (
    path.startsWith('/policy-studio/sources') ||
    path.startsWith('/policy-studio/scoping')
  )
    return ['scoping']
  return []
}

/** A policy back in validation from a send-back goes to whoever sent it, or
 *  to the validation team when the registry has no sender for it (V4, V-N1). */
const returnsTo = (
  row: PolicySummary,
  email: string,
  teams: readonly PolicyRole[],
): boolean =>
  row.stage === 'validation' &&
  !!row.queue.returning &&
  (row.queue.returnTo
    ? row.queue.returnTo.toLowerCase() === email.toLowerCase()
    : teams.includes('validation'))

/** The handoffs to `teams` — or, with none picked, to the stage `path` is on —
 *  and the send-backs `email` made that have come back. */
export const backWithYouItems = (
  path: string,
  rows: PolicySummary[],
  teams: readonly PolicyRole[] = [],
  email = '',
): BackWithYouItem[] => {
  const mine = teams.length ? teams : teamsOfPath(path)
  const handoffs = rows.filter((row) => HANDOFF_STAGES.includes(row.stage))
  return [
    ...rows.filter((row) => returnsTo(row, email, mine)).map(returned),
    ...(mine.includes('scoping')
      ? [
          ...handoffs
            .filter((row) => row.queue.sendBack?.to === 'scoping')
            .map((row) => sentBack(row, 'scoping')),
          ...handoffs
            .filter((row) => row.stage === 'design' && row.queue.unanswered > 0)
            .map(withScoping),
        ]
      : []),
    ...(mine.includes('design')
      ? [
          ...handoffs
            .filter((row) => row.queue.sendBack?.to === 'design')
            .map((row) => sentBack(row, 'design')),
          ...handoffs
            .filter(
              (row) =>
                row.stage === 'design' &&
                row.queue.sendBack?.to !== 'design' &&
                row.queue.unanswered === 0 &&
                row.openQuestions > 0,
            )
            .map(answered),
        ]
      : []),
  ]
}

/** The mention index's id for an open question, as against a comment thread. */
export const QUESTION_THREAD = 'question:'

/** An open thread or question that tags you or a team of yours, as a card
 *  that opens it. A question asked of you, by name or by team, is something
 *  you have been asked (S-N22); it is answered on its page, not replied to. */
export const mentionItem = (
  row: MentionThreadRow,
  tags: readonly string[],
  unread: boolean,
): BackWithYouItem => {
  const team = row.mentions.find(
    (mention) => mention.startsWith('team:') && tags.includes(mention),
  )
  const question = row.id.startsWith(QUESTION_THREAD)
  const verb = question ? 'Asked' : 'Tagged'
  return {
    kind: question ? 'question' : 'mention',
    key: `thread:${row.id}`,
    id: row.policyId,
    title: row.title,
    from: row.author,
    when: formatDistanceToNowStrict(row.lastAt, { addSuffix: true }),
    context: `On ${row.itemName}`,
    detail: row.excerpt,
    note:
      !team ||
      row.mentions.some((m) => !m.startsWith('team:') && tags.includes(m))
        ? `${verb} you`
        : `${verb} @${team.slice('team:'.length)}`,
    noteIcon: question ? 'i-ph-question' : 'i-ph-at',
    action: question ? 'Answer' : 'Open thread',
    to: `${STAGE_PATH[row.stage]}/${row.policyId}?thread=${row.id}`,
    unread,
    ...(question ? {} : { thread: { id: row.id, itemId: row.itemId } }),
  }
}
